import {
  GetValue,
  PartialScreenWithCoveragesRecord,
  ScreenWithCoverages,
} from "../types";
import {
  getGolfCartDamageAndLiabilityCoverageValueFormats,
  getEarthquakeCoverageValueFormats,
  getVacationRentalCoverageValueFormats,
  getWaterBackupCoverageValueFormats,
  getLossAssessmentValueFormats,
  getDebrisRemovalValueFormats,
  getIncidentalFarmingValueFormats,
  getEnhancedCoverageValueFormats,
  getTripCollisionCoverageValueFormats,
  getMineSubsidenceValueFormats,
  getMineSubsidenceScheduledValueFormats,
  getEquipmentBreakdownValueFormats,
  getIdentityFraudExpenseValueFormats,
  getValuableItemProtectionValueFormats,
  getSinkholeCoverageValueFormats,
  getResidenceBurglaryValueFormats,
  getBuildersRiskValueFormats,
  getSecondaryResidenceLiabilityValueFormats,
  getVandalismOrMaliciousMischiefValueFormats,
  getWindstormOrHailExclusionValueFormats,
  getFortifiedRoofUpgradeValueFormats,
} from "./decorators/valueFormats";
import { ExtraCoverageAccessor } from "./extraCoverageAccessor";
import {
  getMineSubsidenceName,
  getMineSubsidenceDescription,
  getMineSubsidenceTooltip,
  MineSubsidenceOption,
} from "./options/mineSubsidence/option";
import {
  getMineSubsidenceScheduledDescription,
  getMineSubsidenceScheduledTooltip,
} from "./options/mineSubsidenceScheduled/option";
import {
  GetExtraCoverageDetailsFactory,
  GetExtraCoverageGetters,
  GetExtraCoverageNameFactory,
  GetExtraCoverageValueFormats,
} from "./types";

const extraCoverageName: Record<
  Exclude<ExtraCoverageAccessor, ExtraCoverageAccessor.MineSubsidence>,
  string
> = {
  [ExtraCoverageAccessor.GolfCartDamageAndLiabilityCoverage]:
    "Golf Cart Damage & Liability Coverage",
  [ExtraCoverageAccessor.EnhancedCoverage]: "Enhanced Coverage",
  [ExtraCoverageAccessor.TripCollisionCoverage]: "Trip Collision Coverage",
  [ExtraCoverageAccessor.EarthquakeCoverage]: "Earthquake Coverage",
  [ExtraCoverageAccessor.VacationRentalCoverage]: "Vacation Rental Coverage",
  [ExtraCoverageAccessor.WaterBackupCoverage]: "Water Backup Coverage",
  [ExtraCoverageAccessor.MineSubsidenceScheduled]:
    "Mine Subsidence - Other Structures - Scheduled",
  [ExtraCoverageAccessor.DebrisRemoval]: "Debris Removal",
  [ExtraCoverageAccessor.LossAssessment]: "Loss Assessment",
  [ExtraCoverageAccessor.IncidentalFarming]: "Incidental Farming",
  [ExtraCoverageAccessor.EquipmentBreakdown]: "Equipment Breakdown",
  [ExtraCoverageAccessor.SinkholeCoverage]: "Sinkhole Coverage",
  [ExtraCoverageAccessor.ValuableItemProtection]: "Valuable Item Protection",
  [ExtraCoverageAccessor.ResidenceBurglary]: "Residence Burglary",
  [ExtraCoverageAccessor.BuildersRisk]: "Builder's Risk",
  [ExtraCoverageAccessor.SecondaryResidenceLiability]:
    "Secondary Residence Liability",
  [ExtraCoverageAccessor.VandalismOrMaliciousMischief]:
    "Vandalism or Malicious Mischief",
  [ExtraCoverageAccessor.WindstormOrHailExclusion]:
    "Windstorm or Hail Exclusion",
  [ExtraCoverageAccessor.FortifiedRoofUpgrade]: "Fortified Roof Upgrade",
  [ExtraCoverageAccessor.IdentityFraudExpense]: "Identity Fraud",
};

const extraCoverageDescription: Record<
  Exclude<
    ExtraCoverageAccessor,
    | ExtraCoverageAccessor.MineSubsidence
    | ExtraCoverageAccessor.MineSubsidenceScheduled
  >,
  PartialScreenWithCoveragesRecord
> = {
  [ExtraCoverageAccessor.GolfCartDamageAndLiabilityCoverage]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides for physical damage to golf carts and extends liability coverage to apply to non-commercial use of golf carts.",
    [ScreenWithCoverages.PlanDetails]:
      "Provides for physical damage to golf carts and extends liability coverage to apply to non-commercial use of golf carts.",
  },
  [ExtraCoverageAccessor.EnhancedCoverage]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Added protection for your personal property like jewelry, golf equipment, electronics, etc.",
    [ScreenWithCoverages.PlanDetails]:
      "Added protection for your personal property like jewelry, golf equipment, electronics, etc.",
  },
  [ExtraCoverageAccessor.TripCollisionCoverage]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides physical damage coverage while the dwelling is being moved.",
    [ScreenWithCoverages.PlanDetails]:
      "Provides physical damage coverage while the dwelling is being moved.",
  },
  [ExtraCoverageAccessor.EarthquakeCoverage]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage for the direct physical loss caused by an earthquake.",
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage for the direct physical loss caused by an earthquake.",
  },
  [ExtraCoverageAccessor.VacationRentalCoverage]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "This provides coverage for when the home is rented partially or in its entirety.",
    [ScreenWithCoverages.PlanDetails]:
      "This provides coverage for when the home is rented partially or in its entirety.",
  },
  [ExtraCoverageAccessor.WaterBackupCoverage]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "This coverage provides protection for your home and personal items against water or sewage that comes through piping.",
    [ScreenWithCoverages.PlanDetails]:
      "This coverage provides protection for your home and personal items against water or sewage that comes through piping.",
  },
  [ExtraCoverageAccessor.DebrisRemoval]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Coverage to assist with the clean-up of debris from a covered loss.",
    [ScreenWithCoverages.PlanDetails]:
      "Coverage to assist with the clean-up of debris from a covered loss.",
  },
  [ExtraCoverageAccessor.LossAssessment]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Coverage assessed by a shared community or homeowners association for damages to the community or HOA's common areas.",
    [ScreenWithCoverages.PlanDetails]:
      "Coverage assessed by a shared community or homeowners association for damages to the community or HOA's common areas.",
  },
  [ExtraCoverageAccessor.IncidentalFarming]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Coverage for small-scale, primarily personal use, non-commercialized farms with less than $5000.00 in income per year.",
    [ScreenWithCoverages.PlanDetails]:
      "Coverage for small-scale, primarily personal use, non-commercialized farms with less than $5000.00 in income per year.",
  },
  [ExtraCoverageAccessor.EquipmentBreakdown]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Covers physical loss or damage from breakdown of equipment like system and appliances in the house.",
    [ScreenWithCoverages.PlanDetails]:
      "Covers physical loss or damage from breakdown of equipment like system and appliances in the house.",
  },
  [ExtraCoverageAccessor.SinkholeCoverage]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage for the collapse of the earth as the result of sinkhole activity.",
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage for the collapse of the earth as the result of sinkhole activity.",
  },
  [ExtraCoverageAccessor.ValuableItemProtection]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Added protection for damage to electronics, tools, and equipment.",
    [ScreenWithCoverages.PlanDetails]:
      "Added protection for damage to electronics, tools, and equipment.",
  },
  [ExtraCoverageAccessor.ResidenceBurglary]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Coverage for the loss of household or personal property from within the residence caused by burglary.",
    [ScreenWithCoverages.PlanDetails]:
      "Coverage for the loss of household or personal property from within the residence caused by burglary.",
  },
  [ExtraCoverageAccessor.BuildersRisk]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage that protects against loss of building materials or supplies at the insured location while the property is under renovation.",
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage that protects against loss of building materials or supplies at the insured location while the property is under renovation.",
  },
  [ExtraCoverageAccessor.SecondaryResidenceLiability]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Coverage that extends liability to your secondary residence.",
    [ScreenWithCoverages.PlanDetails]:
      "Coverage that extends liability to your secondary residence.",
  },
  [ExtraCoverageAccessor.VandalismOrMaliciousMischief]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Coverage that extends liability to your secondary residence.",
    [ScreenWithCoverages.PlanDetails]:
      "Coverage that extends liability to your secondary residence.",
  },
  [ExtraCoverageAccessor.WindstormOrHailExclusion]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "This exclusion removes coverage associated with windstorm or hail.",
    [ScreenWithCoverages.PlanDetails]:
      "This exclusion removes coverage associated with windstorm or hail.",
  },
  [ExtraCoverageAccessor.FortifiedRoofUpgrade]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides for the additional cost to upgrade a roof to FORTIFIED status if a covered loss causes the entire roof to be replaced.",
    [ScreenWithCoverages.PlanDetails]:
      "Provides for the additional cost to upgrade a roof to FORTIFIED status if a covered loss causes the entire roof to be replaced.",
  },
  [ExtraCoverageAccessor.IdentityFraudExpense]: {
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage for expenses incurred as a result of identity fraud. Expenses can include things like notarizing, certified mail, loan application fees, and attorney’s fees.",
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage for expenses incurred as a result of identity fraud. Expenses can include things like notarizing, certified mail, loan application fees, and attorney’s fees.",
  },
};

const extraCoverageTooltip: Record<
  Exclude<
    ExtraCoverageAccessor,
    | ExtraCoverageAccessor.MineSubsidence
    | ExtraCoverageAccessor.MineSubsidenceScheduled
  >,
  PartialScreenWithCoveragesRecord
> = {
  [ExtraCoverageAccessor.GolfCartDamageAndLiabilityCoverage]: {
    [ScreenWithCoverages.PlanDetails]:
      "Acceptable if not driven on public roadways. Not available for policies without liability.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Acceptable if not driven on public roadways. Not available for policies without liability.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides for physical damage to golf carts and extends liability coverage to apply to non-commercial use of golf carts. Acceptable if not driven on public roadways. Not available for policies without liability.",
  },
  [ExtraCoverageAccessor.EnhancedCoverage]: {
    [ScreenWithCoverages.PlanDetails]:
      "This coverage provides a number of enhancements to the policy that insureds don’t always think about but are glad to have when needed. These changes include, but are not limited to, increased limits for certain categories of personal property and changes debris removal limit to 5%.",
    [ScreenWithCoverages.ExtraCoverages]:
      "This coverage provides a number of enhancements to the policy that insureds don’t always think about but are glad to have when needed. These changes include, but are not limited to, increased limits for certain categories of personal property and changes debris removal limit to 5%.",
    [ScreenWithCoverages.PolicySummary]:
      "This coverage provides a number of enhancements to the policy that insureds don’t always think about but are glad to have when needed. These changes include, but are not limited to, increased limits for certain categories of personal property and changes debris removal limit to 5%.",
  },
  [ExtraCoverageAccessor.TripCollisionCoverage]: {
    [ScreenWithCoverages.PlanDetails]:
      "Coverage must be requested before the leveling blocks/jacks are removed and utilities are disconnected to avoid a coverage lapse.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Coverage must be requested before the leveling blocks/jacks are removed and utilities are disconnected to avoid a coverage lapse.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides physical damage coverage while the dwelling is being moved. Coverage must be requested before the leveling blocks/jacks are removed and utilities are disconnected to avoid a coverage lapse.",
  },
  [ExtraCoverageAccessor.EarthquakeCoverage]: {
    [ScreenWithCoverages.PlanDetails]:
      "Coverage does not include landslides, mine subsidence, sinkholes, other land subsidence, or resulting floods.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Coverage does not include landslides, mine subsidence, sinkholes, other land subsidence, or resulting floods.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides coverage for the direct physical loss caused by an earthquake. Coverage does not include landslides, mine subsidence, sinkholes, other land subsidence, or resulting floods.",
  },
  [ExtraCoverageAccessor.VacationRentalCoverage]: {
    [ScreenWithCoverages.PlanDetails]:
      "This provides coverage for when part of the home is rented. Coverage is limited to 30 days a year. If the home is rented longer than that then you will probably want a Landlord/Rental policy combined with a Tenant policy. Contact us if you have questions.",
    [ScreenWithCoverages.ExtraCoverages]:
      "This provides coverage for when part of the home is rented. Coverage is limited to 30 days a year. If the home is rented longer than that then you will probably want a Landlord/Rental policy combined with a Tenant policy. Contact us if you have questions.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides coverage for when part of the home is rented. Coverage is limited to 30 days a year. If the home is rented longer than that then you will probably want a Landlord/Rental policy combined with a Tenant policy. Contact us if you have questions.",
  },
  [ExtraCoverageAccessor.WaterBackupCoverage]: {
    [ScreenWithCoverages.PlanDetails]:
      "This could be the result of aging piping, tree roots, or even mechanical failure of a pump.",
    [ScreenWithCoverages.ExtraCoverages]:
      "This could be the result of aging piping, tree roots, or even mechanical failure of a pump.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides protection for your home and personal items against water or sewage that comes through piping. This could be the result of aging piping, tree roots, or even mechanical failure of a pump.",
  },
  [ExtraCoverageAccessor.DebrisRemoval]: {
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage to assist with the clean-up of debris from a covered loss.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage to assist with the clean-up of debris from a covered loss.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides coverage to assist with the clean-up of debris from a covered loss.",
  },
  [ExtraCoverageAccessor.LossAssessment]: {
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage to pay for fees assessed by a shared community or homeowners association for damages to the community or HOA's common areas.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage to pay for fees assessed by a shared community or homeowners association for damages to the community or HOA's common areas.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides coverage to pay for fees assessed by a shared community or homeowners association for damages to the community or HOA's common areas.",
  },
  [ExtraCoverageAccessor.IncidentalFarming]: {
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage to offset loss for small-scale, primarily personal use, non-commercialized farms with less than $5000.00 in income per year.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage to offset loss for small-scale, primarily personal use, non-commercialized farms with less than $5000.00 in income per year.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides coverage to offset loss for small-scale, primarily personal use, non-commercialized farms with less than $5000.00 in income per year.",
  },
  [ExtraCoverageAccessor.EquipmentBreakdown]: {
    [ScreenWithCoverages.PlanDetails]:
      "Equipment breakdown coverage is for loss due to mechanical or electrical breakdown of nearly any type of equipment. Coverage applies to the cost to repair or replace the equipment and any other property damaged by the equipment breakdown.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Equipment breakdown coverage is for loss due to mechanical or electrical breakdown of nearly any type of equipment. Coverage applies to the cost to repair or replace the equipment and any other property damaged by the equipment breakdown.",
    [ScreenWithCoverages.PolicySummary]:
      "Equipment breakdown coverage is for loss due to mechanical or electrical breakdown of nearly any type of equipment. Coverage applies to the cost to repair or replace the equipment and any other property damaged by the equipment breakdown.",
  },
  [ExtraCoverageAccessor.SinkholeCoverage]: {
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage for damages to a structure or property caused by a collapse of the earth as the result of sinkhole activity.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage for damages to a structure or property caused by a collapse of the earth as the result of sinkhole activity.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides coverage for damages caused by a collapse of the earth as the result of sinkhole activity.",
  },
  [ExtraCoverageAccessor.ValuableItemProtection]: {
    [ScreenWithCoverages.PlanDetails]:
      "Itemized coverage for personal effects such as jewelry, firearms, tools, camera, or computer equipment.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Itemized coverage for personal effects such as jewelry, firearms, tools, camera, or computer equipment.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides coverage for specific valuable items such as jewelry, firearms, tools, camera, or computer equipment.",
  },
  [ExtraCoverageAccessor.ResidenceBurglary]: {
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage for the loss of household or personal property from within the residence caused by burglary.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage for the loss of household or personal property from within the residence caused by burglary.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides coverage for the loss of household or personal property caused by burglary.",
  },
  [ExtraCoverageAccessor.BuildersRisk]: {
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage that protects against loss of building materials or supplies at the insured location while the property is under renovation.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage that protects against loss of building materials or supplies at the insured location while the property is under renovation.",
    [ScreenWithCoverages.PolicySummary]:
      "Protects against loss of building materials or supplies while the property is under renovation.",
  },
  [ExtraCoverageAccessor.SecondaryResidenceLiability]: {
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage that extends liability to your secondary residence.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage that extends liability to your secondary residence.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides coverage that extends liability to your secondary residence.",
  },
  [ExtraCoverageAccessor.VandalismOrMaliciousMischief]: {
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage that protects against losses sustained as a result of vandals.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage that protects against losses sustained as a result of vandals.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides coverage that protects against losses sustained as a result of vandals.",
  },
  [ExtraCoverageAccessor.WindstormOrHailExclusion]: {
    [ScreenWithCoverages.PlanDetails]:
      "This exclusion removes coverage for damage associated with windstorm or hail and subsequent losses such as loss of use and rain damage.",
    [ScreenWithCoverages.ExtraCoverages]:
      "This exclusion removes coverage for damage associated with windstorm or hail and subsequent losses such as loss of use and rain damage.",
    [ScreenWithCoverages.PolicySummary]:
      "This exclusion removes coverage for damage associated with windstorm or hail and subsequent losses such as loss of use and rain damage.",
  },
  [ExtraCoverageAccessor.FortifiedRoofUpgrade]: {
    [ScreenWithCoverages.PlanDetails]:
      "Provides for the additional cost to upgrade a roof to FORTIFIED status if a covered loss causes the entire roof to be replaced.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides for the additional cost to upgrade a roof to FORTIFIED status if a covered loss causes the entire roof to be replaced.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides for the additional cost to upgrade a roof to FORTIFIED status if a covered loss causes the entire roof to be replaced.",
  },
  [ExtraCoverageAccessor.IdentityFraudExpense]: {
    [ScreenWithCoverages.PlanDetails]:
      "Provides coverage for expenses incurred as a result of identity fraud. Expenses can include things like notarizing, certified mail, loan application fees, and attorney’s fees.",
    [ScreenWithCoverages.ExtraCoverages]:
      "Provides coverage for expenses incurred as a result of identity fraud. Expenses can include things like notarizing, certified mail, loan application fees, and attorney’s fees.",
    [ScreenWithCoverages.PolicySummary]:
      "Provides coverage for expenses incurred as a result of identity fraud. Expenses can include things like notarizing, certified mail, loan application fees, and attorney’s fees.",
  },
};

const extraCoverageValueFormats: Record<ExtraCoverageAccessor, GetValue> = {
  [ExtraCoverageAccessor.GolfCartDamageAndLiabilityCoverage]:
    getGolfCartDamageAndLiabilityCoverageValueFormats,
  [ExtraCoverageAccessor.EarthquakeCoverage]: getEarthquakeCoverageValueFormats,
  [ExtraCoverageAccessor.VacationRentalCoverage]:
    getVacationRentalCoverageValueFormats,
  [ExtraCoverageAccessor.WaterBackupCoverage]:
    getWaterBackupCoverageValueFormats,
  [ExtraCoverageAccessor.LossAssessment]: getLossAssessmentValueFormats,
  [ExtraCoverageAccessor.DebrisRemoval]: getDebrisRemovalValueFormats,
  [ExtraCoverageAccessor.IncidentalFarming]: getIncidentalFarmingValueFormats,
  [ExtraCoverageAccessor.EnhancedCoverage]: getEnhancedCoverageValueFormats,
  [ExtraCoverageAccessor.TripCollisionCoverage]:
    getTripCollisionCoverageValueFormats,
  [ExtraCoverageAccessor.MineSubsidence]: getMineSubsidenceValueFormats,
  [ExtraCoverageAccessor.MineSubsidenceScheduled]:
    getMineSubsidenceScheduledValueFormats,
  [ExtraCoverageAccessor.EquipmentBreakdown]: getEquipmentBreakdownValueFormats,
  [ExtraCoverageAccessor.IdentityFraudExpense]:
    getIdentityFraudExpenseValueFormats,
  [ExtraCoverageAccessor.ValuableItemProtection]:
    getValuableItemProtectionValueFormats,
  [ExtraCoverageAccessor.SinkholeCoverage]: getSinkholeCoverageValueFormats,
  [ExtraCoverageAccessor.ResidenceBurglary]: getResidenceBurglaryValueFormats,
  [ExtraCoverageAccessor.BuildersRisk]: getBuildersRiskValueFormats,
  [ExtraCoverageAccessor.SecondaryResidenceLiability]:
    getSecondaryResidenceLiabilityValueFormats,
  [ExtraCoverageAccessor.VandalismOrMaliciousMischief]:
    getVandalismOrMaliciousMischiefValueFormats,
  [ExtraCoverageAccessor.WindstormOrHailExclusion]:
    getWindstormOrHailExclusionValueFormats,
  [ExtraCoverageAccessor.FortifiedRoofUpgrade]:
    getFortifiedRoofUpgradeValueFormats,
};

const getExtraCoverageName: GetExtraCoverageNameFactory =
  (accessor) =>
  (options = {}) => {
    const { mineSubsidenceOption } = options;

    if (accessor === ExtraCoverageAccessor.MineSubsidence) {
      return mineSubsidenceOption
        ? getMineSubsidenceName(mineSubsidenceOption)
        : getMineSubsidenceName(MineSubsidenceOption.Default);
    }

    return extraCoverageName[accessor];
  };

const getExtraCoverageTooltip: GetExtraCoverageDetailsFactory =
  (accessor, screen) =>
  (options = {}) => {
    const { mineSubsidenceOption, mineSubsidenceScheduledOption } = options;

    if (accessor === ExtraCoverageAccessor.MineSubsidence) {
      return mineSubsidenceOption
        ? getMineSubsidenceTooltip(mineSubsidenceOption, screen)
        : undefined;
    }

    if (accessor === ExtraCoverageAccessor.MineSubsidenceScheduled) {
      return mineSubsidenceScheduledOption
        ? getMineSubsidenceScheduledTooltip(
            mineSubsidenceScheduledOption,
            screen
          )
        : undefined;
    }

    return extraCoverageTooltip[accessor][screen];
  };

const getExtraCoverageDescription: GetExtraCoverageDetailsFactory =
  (accessor, screen) =>
  (options = {}) => {
    const { mineSubsidenceOption, mineSubsidenceScheduledOption } = options;

    if (accessor === ExtraCoverageAccessor.MineSubsidence) {
      return mineSubsidenceOption
        ? getMineSubsidenceDescription(mineSubsidenceOption, screen)
        : undefined;
    }

    if (accessor === ExtraCoverageAccessor.MineSubsidenceScheduled) {
      return mineSubsidenceScheduledOption
        ? getMineSubsidenceScheduledDescription(
            mineSubsidenceScheduledOption,
            screen
          )
        : undefined;
    }

    return extraCoverageDescription[accessor][screen];
  };

const getExtraCoverageValueFormats: GetExtraCoverageValueFormats = (
  accessor
) => {
  return extraCoverageValueFormats[accessor];
};

const getExtraCoverageGetters: GetExtraCoverageGetters = (
  accessor,
  screen
) => ({
  getName: getExtraCoverageName(accessor),
  getValueFormats: getExtraCoverageValueFormats(accessor),
  getDescription: getExtraCoverageDescription(accessor, screen),
  getTooltip: getExtraCoverageTooltip(accessor, screen),
});

export {
  getExtraCoverageGetters,
  extraCoverageName,
  extraCoverageDescription,
  extraCoverageTooltip,
  extraCoverageValueFormats,
};
